/**
 * @file DOM 相关方法
 * @author dafo<huanghouin@baijiahulian.com>
 * @module util/dom
 */

/**
 * 为 HTML 元素绑定事件
 *
 * @function
 * @param {Element} el HTML 元素
 * @param {string} name 事件名
 * @param {Function} handler 事件处理函数
 */
export const on = (el, name, handler, useCapture = false) => {
    el && el.addEventListener(name, handler, useCapture);
};

/**
 * 为 HTML 元素解除事件绑定
 *
 * @function
 * @param {Element} el HTML 元素
 * @param {string} name 事件名
 * @param {Function} handler 已经绑定的事件处理函数
 */
export const off = (el, name, handler, useCapture = false) => {
    el && el.removeEventListener(name, handler, useCapture);
};

/**
 *  测试 HTML 元素的 class 中是否包含指定的 class
 *
 * @function
 * @param {Element} el HTML 元素
 * @param {string} className 测试是否包含的 class
 * @return {boolean} 如果包含指定的 class，返回 true，否则 false
 */
export const hasClass = typeof document !== 'undefined' && 'classList' in document.documentElement
    ? (el, className) => el.classList.contains(className)
    : (el, className) => ~` ${el.className} `.indexOf(` ${className} `);

/* eslint-disable require-unicode-regexp */
/**
 * 区配选择器的正则
 *
 * @const {RegExp}
 */
const SELECTOR_PATTERN = /^([a-z][a-z\d-]*)?(?:\.([a-z\d-_.]+))?(?:\[([^=\]]+)(?:=([^\]]+))?\])?$/i;
/* eslint-enable require-unicode-regexp */

/**
 * 向上查找匹配指定选择器的元素
 *
 * @param {Element} el 查找的起始 HTML 元素
 * @param {string} selector 要匹配的选择器
 * @return {?Element} 匹配到元素时返回该元素，否则返回 null
 */
export function closest(el, selector) {
    let [
        ,
        tag = '',
        className = '',
        attrName = '',
        attrValue = ''
    ] = selector.match(SELECTOR_PATTERN);

    tag = tag.toUpperCase();
    attrValue = attrValue.replace(/^(['"])(.+)\1$/u, '$2');

    const is = function (el) {
        return el
            && (!tag || el.tagName === tag)
            && (!className || el.className && hasClass(el, className))
            && (!attrName
                || attrName && !attrValue && el.hasAttribute(attrName)
                || attrName && attrValue && el.getAttribute(attrName, 2) === attrValue
            );
    };

    let parent = el;
    while (parent && parent.tagName !== 'HTML') {
        if (is(parent)) {
            return parent;
        }

        parent = parent.parentNode;
    }
}

/**
 * 获取指定 URL 的 pathname
 *
 * @function
 * @param {string} [url=location.href]  要处理的 URL
 * @return {string} 移除域名后的 pathname，SPA 页面取第一个 # 后面的路径
 */
export const getPageURL = (url = location.href) => url.replace(/^.*\/\/[^/]+(\/#)?(?=\/)/u, '');

/**
 * 视窗范围
 *
 * @typedef {Object} ViewPort
 * @property {number} left 左边界
 * @property {number} right 左边界
 * @property {number} top 上边界
 * @property {number} bottom 下边界
 */

/**
 * 获取当前视窗范围
 *
 * @function
 * @return {ViewPort} 返回当前视窗的坐标值
*/
export const getViewPort = () => {
    const {
        scrollLeft,
        scrollTop,
        clientWidth,
        clientHeight
    } = document.documentElement;
    return {
        left: scrollLeft,
        right: scrollLeft + clientWidth,
        top: scrollTop,
        bottom: scrollTop + clientHeight
    };
};

/**
 * 判断指定 HTML 元素是否在视窗内
 *
 * @function
 * @param {Element} el 待判断的 HTML 元素
 * @return {boolean} el 在视窗内时返回 true，否则返回 false
 */
export const isInViewPort = el => {
    const html = document.documentElement;
    const {body} = document;

    const viewPort = {
        top: 0,
        left: 0,
        right: html.clientWidth || body.clientWidth,
        bottom: html.clientHeight || body.clientHeight
    };
    const targetRect = el.getBoundingClientRect();

    const intersectWidth = (viewPort.right > targetRect.right ? targetRect.right : viewPort.right)
        - (viewPort.left > targetRect.left ? viewPort.left : targetRect.left);
    const intersectHeight = (viewPort.bottom > targetRect.bottom ? targetRect.bottom : viewPort.bottom)
        - (viewPort.top > targetRect.top ? viewPort.top : targetRect.top);

    return intersectWidth > 0 && intersectHeight > 0;
};
